import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { LiveAvatarSession } from "@heygen/liveavatar-web-sdk";
import { useTheme } from "../../../hooks/useTheme";
import { useInterviewFlow } from "./hooks/useInterviewFlow";
import CameraView from "./components/CameraView";
import AlertPanel from "./components/AlertPanel";
import ProctoringAlertModal from "./components/ProctoringAlertModal";
import { useScreenProctoring } from "../../../components/Instructions/hooks/useScreenProctoring";
import {
  createLiveAvatarSession,
  completeLiveAvatarSession,
  disconnectLiveAvatarSession,
  initiateLiveAvatarRecording,
  getLiveAvatarRecordingPartUrl,
  completeLiveAvatarRecording,
} from "./services/liveAvatarService";
import { getConfigurations } from "./services/interviewService";
import { getPreferredAudioConstraints } from "../../../audio-kernel/micPreference";
import "./interview.css";

// ─── Dev flag ─────────────────────────────────────────────────────────────────
const IS_DEV = true; // flip to true locally to show dev console

// ─── Debug console (dev only) ─────────────────────────────────────────────────
const DBG_BTN_CLS = "font-mono text-xs text-green-600 hover:text-green-300 transition-colors";

// Works even when document focus is lost (fullscreen interviews lose focus on clipboard API calls)
function copyToClipboard(text) {
  if (navigator.clipboard && document.hasFocus()) {
    return navigator.clipboard.writeText(text);
  }
  // execCommand fallback — works without document focus
  const el = document.createElement("textarea");
  el.value = text;
  el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
  document.body.appendChild(el);
  el.focus();
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
  return Promise.resolve();
}

function DebugConsole({ logs, onClear }) {
  const [copied, setCopied] = React.useState(false);

  function handleCopy() {
    const text = [...logs].reverse()
      .map((e) => `[${e.time}] ${e.label}${e.data ? " " + e.data : ""}`)
      .join("\n");
    copyToClipboard(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div
      style={{ position: "fixed", top: 8, left: 8, width: 320, zIndex: 40, pointerEvents: "auto" }}
      className="bg-black/75 rounded-lg shadow-xl border border-green-700/40 flex flex-col"
    >
      <div className="flex items-center justify-between px-3 py-1 border-b border-green-700/40">
        <span className="font-mono text-xs text-green-400 font-bold tracking-widest">DEV CONSOLE</span>
        <div className="flex gap-2">
          <button onClick={handleCopy} className={DBG_BTN_CLS}>{copied ? "copied!" : "copy"}</button>
          <button onClick={onClear}    className={DBG_BTN_CLS}>clear</button>
        </div>
      </div>
      <div className="overflow-y-auto flex flex-col-reverse" style={{ maxHeight: 240 }}>
        {logs.length === 0 ? (
          <p className="font-mono text-xs text-green-700 px-3 py-2">— waiting for events —</p>
        ) : (
          logs.map((entry, i) => (
            <div key={i} className="px-3 py-0.5 border-b border-green-900/30 last:border-0">
              <span className="font-mono text-xs text-green-600">[{entry.time}]</span>{" "}
              <span className="font-mono text-xs text-green-300">{entry.label}</span>
              {entry.data ? (
                <span className="font-mono text-xs text-green-500"> {entry.data}</span>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PHASES = {
  INIT:       "init",
  STARTING:   "starting",
  LIVE:       "live",
  COMPLETING: "completing",
  ERROR:      "error",
};

const CONNECTING_STEPS = [
  "Your interviewer is joining...",
  "Almost ready, just a moment...",
];

const PIP_WIDTH  = 300; // px
const PIP_HEIGHT = 180; // px

// Full-screen modal overlay wrapper — shared by proctoring alert and screen-share-stop modals
const ModalOverlay = ({ children }) => (
  <div className="absolute inset-0 z-50 flex items-center justify-center">{children}</div>
);

export default function LiveAvatarFullModePortal() {
  const { interview_id } = useParams();
  const navigate          = useNavigate();
  const { theme }         = useTheme();
  const isTabSwitchingEnabled = useSelector((state) => state.miscSlice.isTabSwitchingEnabled);

  // ── Phase & UI state ────────────────────────────────────────────────
  const [phase, setPhase]         = useState(PHASES.INIT);
  const [errorMessage, setErrorMessage] = useState(null);
  const [connectingStep, setConnectingStep] = useState(0);
  const [connectionQuality, setConnectionQuality] = useState(null); // null | "EXCELLENT" | "GOOD" | "POOR"
  const [showStatusHint, setShowStatusHint] = useState(true);
  const [readyToInit, setReadyToInit] = useState(!!document.fullscreenElement);
  const isLive    = phase === PHASES.LIVE;

  // Show "Speak clearly" hint for 10s from mount then hide
  useEffect(() => {
    const t = setTimeout(() => setShowStatusHint(false), 10000);
    return () => clearTimeout(t);
  }, []);

  // ── Debug console (dev only) ─────────────────────────────────────────
  const [debugLogs, setDebugLogs] = useState([]);
  const dbg = useCallback((label, data) => {
    if (!IS_DEV) return;
    const time = new Date().toTimeString().slice(0, 8);
    const dataStr = data !== undefined
      ? String(typeof data === "object" ? JSON.stringify(data) : data).slice(0, 80)
      : "";
    setDebugLogs((prev) => [{ time, label, data: dataStr }, ...prev].slice(0, 50));
  }, []);

  // ── Avatar video — SDK attaches to this element ───────────────────
  const avatarVideoRef = useRef(null);

  // ── PIP video — separate element that mirrors the proctoring stream ──
  const pipVideoRef = useRef(null);

  // ── Session refs ─────────────────────────────────────────────────────
  const sessionRef     = useRef(null);
  const interviewIdRef = useRef(interview_id);
  const completingRef  = useRef(false);
  const initDoneRef    = useRef(false);
  const unmountingRef  = useRef(false);

  // ── Proctoring ───────────────────────────────────────────────────────
  const { state, actions, refs, getActiveAlerts } = useInterviewFlow(
    interview_id,
    navigate,
    { isLiveAvatar: true }
  );
  const { proctoringVideoRef } = refs;
  const {
    isInterviewActive,
    showProctoringAlert,
    proctoringAlertMessage,
    alertLog,
    currentAlertIndex,
    isAlertPanelOpen,
    stickyAlertCount,
    storingEnabled,
  } = state;

  // ── storingEnabled ref — populated before session starts to avoid stale closure
  const storingEnabledRef = useRef(false);

  // ── Recording refs (multipart S3 upload + intro clip) ────────────────
  const mediaRecorderRef    = useRef(null);
  const micStreamRef        = useRef(null);
  const uploadIdRef         = useRef(null);
  const uploadedPartsRef    = useRef([]);
  const partBufferRef       = useRef([]);
  const partBufferSizeRef   = useRef(0);
  const partNumberRef       = useRef(1);
  const isUploadingPartRef  = useRef(false);
  const introRecorderRef    = useRef(null); // stopped explicitly if interview ends before 2 min
  const introTimerRef       = useRef(null); // cancelled on early unmount
  const {
    handleFaceViolation,
    handleMultipleFaceViolation,
    handleProhibitedObjectViolation,
    handleBodyPostureViolation,
    handleGazeViolation,
    handleStickyAlert,
    handleFullscreenExitViolation,
    handleTabSwitchViolation,
    handleScreenShareStopedViolation,
    handleProctoringAcknowledge,
    activateProctoring,
    setIsIntentionalFullscreenChange,
    handleAlertIconClick,
    navigateToNextAlert,
    navigateToPrevAlert,
    handleAlertClick,
  } = actions;

  const {
    isScreenSharing,
    requestScreenPermission,
  } = useScreenProctoring({ checkInterval: 3000, autoStart: true });

  // Stable no-ops — avoids new function refs on every render
  const noop = useCallback(() => {}, []);

  // ── Mirror proctoring stream → PIP video ─────────────────────────────
  // Poll with rAF until CameraView sets srcObject on proctoringVideoRef, then copy it to pipVideoRef.
  useEffect(() => {
    if (!isLive) return;
    let rafId;
    function sync() {
      const src = proctoringVideoRef.current?.srcObject;
      if (src && pipVideoRef.current && pipVideoRef.current.srcObject !== src) {
        pipVideoRef.current.srcObject = src;
        pipVideoRef.current.play().catch(() => {});
        return; // done
      }
      rafId = requestAnimationFrame(sync);
    }
    rafId = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(rafId);
  }, [isLive]); // proctoringVideoRef is a stable ref object — not a dependency

  // ── Rotate connecting step message (stops once live) ─────────────────
  useEffect(() => {
    if (isLive) return;
    const interval = setInterval(() => {
      setConnectingStep((prev) => (prev + 1) % CONNECTING_STEPS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isLive]);

  // ── Pause Anna when candidate leaves (tab hidden or proctoring modal up) ──
  // Goal: Anna must not talk into the void / capture background speech. On leave we
  // interrupt her, stop STT and mute the mic; on return we unmute, resume listening,
  // and re-speak her last question via repeat() (TTS) so the candidate misses nothing.
  const isPausedRef = useRef(false);
  const resumeTimerRef = useRef(null);
  // Anna's last spoken line — captured from avatar.transcription, re-spoken via repeat() on return.
  const lastAvatarUtteranceRef = useRef("");
  // "Candidate is away" = a blocking proctoring modal is up OR the tab is hidden.
  const modalUp = showProctoringAlert || (!isScreenSharing && isTabSwitchingEnabled);

  useEffect(() => {
    if (!isLive) return;

    const pauseNow = () => {
      const session = sessionRef.current;
      if (!session || isPausedRef.current) return;
      isPausedRef.current = true;
      try { session.interrupt(); }       catch {}
      try { session.stopListening(); }   catch {}
      try { session.voiceChat?.mute(); } catch {}
      dbg("avatar paused (candidate away)");
    };

    const resumeNow = () => {
      const session = sessionRef.current;
      if (!session || !isPausedRef.current) return;
      isPausedRef.current = false;
      try { session.voiceChat?.unmute(); } catch {}
      try { session.startListening(); }    catch {}
      // Re-speak Anna's last question as TTS only so the candidate doesn't miss it.
      // repeat() does NOT create a user turn, so it never pollutes the transcript.
      const last = lastAvatarUtteranceRef.current;
      if (last) { try { session.repeat(last); } catch {} }
      dbg("avatar resumed (candidate back)");
    };

    const applyPauseState = () => {
      const away = modalUp || document.hidden;
      clearTimeout(resumeTimerRef.current);
      if (away) {
        pauseNow(); // pause immediately
      } else {
        // Debounce resume: only resume after staying "back" continuously for 1.5s,
        // so a brief flicker doesn't rapid-fire pause/resume + repeat().
        resumeTimerRef.current = setTimeout(resumeNow, 1500);
      }
    };

    document.addEventListener("visibilitychange", applyPauseState);
    applyPauseState(); // run once for the current modal state
    return () => {
      document.removeEventListener("visibilitychange", applyPauseState);
      clearTimeout(resumeTimerRef.current);
    };
  }, [isLive, modalUp]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Recording helpers (multipart S3 upload, runs in background) ──────
  const MULTIPART_MIN_SIZE = 5 * 1024 * 1024; // 5 MB — S3 minimum part size
  const INTRO_DURATION_MS  = 2 * 60 * 1000;   // 2 minutes

  async function uploadCurrentPart(isFinal) {
    if (isUploadingPartRef.current) return;
    if (partBufferRef.current.length === 0) return;
    if (!isFinal && partBufferSizeRef.current < MULTIPART_MIN_SIZE) return;

    isUploadingPartRef.current = true;
    const blob = new Blob(partBufferRef.current, { type: "video/webm" });
    partBufferRef.current     = [];
    partBufferSizeRef.current = 0;
    const pn = partNumberRef.current;
    partNumberRef.current++;

    try {
      const { data } = await getLiveAvatarRecordingPartUrl(interview_id, uploadIdRef.current, pn);
      const res = await fetch(data.presigned_url, {
        method: "PUT", body: blob, headers: { "Content-Type": "video/webm" },
      });
      const etag = res.headers.get("ETag");
      if (etag) uploadedPartsRef.current.push({ PartNumber: pn, ETag: etag });
      dbg(`part ${pn} uploaded`, `${(blob.size / 1024 / 1024).toFixed(1)}MB`);
    } catch (err) {
      dbg(`part ${pn} upload failed`, err.message);
    } finally {
      isUploadingPartRef.current = false;
    }
  }

  async function startRecording() {
    if (!storingEnabledRef.current) return;
    try {
      const { data } = await initiateLiveAvatarRecording(interview_id);
      uploadIdRef.current = data.upload_id;

      // Records with the device that passed the mic test (invariant I1);
      // {audio:true} exactly as before when no preference was persisted.
      const micStream = await navigator.mediaDevices.getUserMedia(
        getPreferredAudioConstraints(interview_id)
      );
      micStreamRef.current = micStream;

      const videoTracks = proctoringVideoRef.current?.srcObject?.getVideoTracks() ?? [];
      const combined = new MediaStream([...videoTracks, ...micStream.getAudioTracks()]);
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : "video/webm";
      const recorderOpts = { mimeType, videoBitsPerSecond: 500_000, audioBitsPerSecond: 64_000 };

      // ── Full-session recorder (multipart) ─────────────────────────────
      const recorder = new MediaRecorder(combined, recorderOpts);
      recorder.ondataavailable = (e) => {
        if (e.data.size === 0) return;
        partBufferRef.current.push(e.data);
        partBufferSizeRef.current += e.data.size;
        if (partBufferSizeRef.current >= MULTIPART_MIN_SIZE) {
          uploadCurrentPart(false);
        }
      };
      recorder.start(10_000);
      mediaRecorderRef.current = recorder;

      // ── Intro recorder (2-min clip, single blob upload) ───────────────
      const introChunks = [];
      const introRecorder = new MediaRecorder(combined, recorderOpts);
      introRecorder.ondataavailable = (e) => { if (e.data.size > 0) introChunks.push(e.data); };
      introRecorder.start(10_000);
      introRecorderRef.current = introRecorder;

      introTimerRef.current = setTimeout(async () => {
        if (introRecorder.state === "inactive") return;
        await new Promise((r) => { introRecorder.onstop = r; introRecorder.stop(); });
        const blob = new Blob(introChunks, { type: "video/webm" });
        if (blob.size === 0) return;
        try {
          await fetch(data.intro_presigned_url, { method: "PUT", body: blob, headers: { "Content-Type": "video/webm" } });
          dbg("intro uploaded", `${(blob.size / 1024 / 1024).toFixed(1)}MB`);
        } catch (err) { dbg("intro upload failed", err.message); }
      }, INTRO_DURATION_MS);

      dbg("recording started");
    } catch (err) {
      dbg("recording start failed", err.message);
    }
  }

  async function finishRecording(interviewId) {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    try {
      await new Promise((resolve) => { recorder.onstop = resolve; recorder.stop(); });
      micStreamRef.current?.getTracks().forEach((t) => t.stop());
      // Stop intro recorder if interview ends before the 2-min clip completes
      clearTimeout(introTimerRef.current);
      if (introRecorderRef.current?.state !== "inactive") introRecorderRef.current?.stop();
      // Wait for any in-progress part upload before flushing the final chunk
      while (isUploadingPartRef.current) { await new Promise((r) => setTimeout(r, 100)); }
      await uploadCurrentPart(true); // upload remaining buffer as final part (can be < 5MB)
      if (uploadedPartsRef.current.length === 0) return;
      await completeLiveAvatarRecording(interviewId, {
        upload_id: uploadIdRef.current,
        parts:     uploadedPartsRef.current,
      });
      dbg("recording complete");
    } catch (err) {
      dbg("recording finish failed", err.message);
    }
  }

  // ── Session completion — navigates to thank-you immediately, backend runs in background ──
  const handleComplete = useCallback(() => {
    if (completingRef.current) return;
    completingRef.current = true;

    finishRecording(interview_id); // fire-and-forget — SPA nav keeps the fetch alive
    // Fire-and-forget — candidate doesn't wait for transcript extraction or scoring
    completeLiveAvatarSession(interview_id).catch(() => {});
    sessionRef.current?.stop().catch(() => {});

    navigate(`/thankyou/${interview_id}`);
  }, [interview_id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Begin Interview handler (direct URL access — not already fullscreen) ──
  const handleBeginInterview = useCallback(async () => {
    await document.documentElement.requestFullscreen().catch(() => {});
    setReadyToInit(true);
  }, []);

  // ── Main init effect ──────────────────────────────────────────────────
  useEffect(() => {
    if (!readyToInit) return;      // wait until user is ready
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    let session = null;
    let closingTimer = null; // hoisted so cleanup can clear it

    async function init() {
      try {
        // Fetch config once here — sets ref before stream_ready fires AND updates hook state
        try {
          const config = await getConfigurations(interview_id);
          storingEnabledRef.current = config.data.storing_enabled ?? false;
          actions.fetchConfigurations(); // sync hook state (storingEnabled, videoIncluded, etc.) from same endpoint
        } catch (_) {
          storingEnabledRef.current = false;
        }

        const { data } = await createLiveAvatarSession(interview_id);
        const { session_token } = data;
        dbg("phase → STARTING", "session created");
        setPhase(PHASES.STARTING);

        session = new LiveAvatarSession(session_token, { voiceChat: true });
        sessionRef.current = session;

        session.on("session.stream_ready", () => {
          if (avatarVideoRef.current) {
            session.attach(avatarVideoRef.current);
          }
          setPhase(PHASES.LIVE);
          dbg("phase → LIVE", "stream_ready");
          activateProctoring();
          startRecording();
          // Delay screen-share prompt so it doesn't interrupt Anna's opening greeting
          setTimeout(() => { requestScreenPermission(); }, 5000);
        });

        session.on("session.state_changed", (s) => {
          dbg("state_changed", s);
        });

        // quality is emitted directly as a string: "UNKNOWN" | "GOOD" | "BAD"
        session.on("session.connection_quality_changed", (quality) => {
          dbg("connection_quality_changed", quality);
          setConnectionQuality(typeof quality === "string" ? quality : null);
        });

        session.on("user.transcription", (event) => {
          dbg("user.transcription", event?.text);
        });

        // ── Detect closing phrase in avatar speech ────────────────────────────
        // Chunks arrive partially, so we use a 300-char rolling buffer to catch
        // "the interview is now complete" across boundaries. Once detected, we
        // debounce 1.5s (wait for Anna to finish speaking) then call handleComplete.
        let avatarBuffer = "";
        let closingPhraseDetected = false;
        session.on("avatar.transcription", (event) => {
          const chunk = event?.text || "";
          dbg("avatar.transcription", chunk);

          // Each chunk is a fairly complete line — keep the latest as the re-askable
          // question (skip the closing line). If interrupted, this is the best text we got.
          if (chunk.trim() && !chunk.toLowerCase().includes("interview is now complete")) {
            lastAvatarUtteranceRef.current = chunk.trim();
          }

          avatarBuffer = (avatarBuffer + " " + chunk).slice(-300).toLowerCase();

          const isClosing =
            avatarBuffer.includes("this interview is now complete") ||
            avatarBuffer.includes("the interview is now complete") ||
            avatarBuffer.includes("interview is now complete");

          if (isClosing) {
            if (!closingPhraseDetected) {
              dbg("closing phrase detected — waiting for speech to finish");
            }
            avatarBuffer = "";          // prevent re-detecting on next chunk
            closingPhraseDetected = true;
          }

          if (closingPhraseDetected) {
            // Reset timer on every new chunk — fire only after speech has gone silent
            clearTimeout(closingTimer);
            closingTimer = setTimeout(() => {
              dbg("closing speech finished → handleComplete");
              handleComplete();
            }, 1500);
          }
        });

        // ── Fallback disconnect handler ───────────────────────────────────────
        session.on("session.disconnected", (reason) => {
          dbg("session.disconnected", reason);

          if (completingRef.current) return;  // already completing
          if (unmountingRef.current) return;   // component unmounting
          if (reason === "SESSION_START_FAILED" || reason === "CLIENT_INITIATED") return;

          // IDLE_TIMEOUT / UNKNOWN_REASON → resume path (keep-alive ON HOLD, see doc §16)
          if (reason === "IDLE_TIMEOUT" || reason === "UNKNOWN_REASON") {
            dbg("idle/unknown → disconnect beacon");
            disconnectLiveAvatarSession(interviewIdRef.current);
            return;
          }

          // MAX_DURATION_REACHED → 20-min plan cap hit, treat as complete
          dbg("max_duration → handleComplete");
          handleComplete();
        });

        await session.start();

      } catch (err) {
        dbg("init error", err?.message);
        const detail = err?.response?.data?.detail || "";
        if (detail.includes("already completed")) {
          completingRef.current = true; // suppress disconnect beacon on unmount
          navigate(`/thankyou/${interview_id}`);
          return;
        }
        setPhase(PHASES.ERROR);
        setErrorMessage(detail || err.message || "Failed to start interview session.");
      }
    }

    init();

    return () => {
      unmountingRef.current = true;
      clearTimeout(closingTimer);
      clearTimeout(introTimerRef.current);
      if (introRecorderRef.current?.state !== "inactive") introRecorderRef.current?.stop();
      if (!completingRef.current) {
        finishRecording(interviewIdRef.current); // best-effort — may not complete if tab is closing
        disconnectLiveAvatarSession(interviewIdRef.current); // unclean exit only
      }
      session?.stop();
    };
  }, [readyToInit, interview_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = phase === PHASES.INIT || phase === PHASES.STARTING;

  // ── Single return — video always mounted so SDK can attach to it ──────
  return (
    <div className={`interview-no-select h-full relative overflow-hidden ${theme === "light" ? "bg-[#FAF9F6]" : "bg-gray-900"}`}>

      {/* Avatar video — always in DOM so SDK attach() always has a target */}
      <div className="absolute inset-0">
        <video
          ref={avatarVideoRef}
          autoPlay
          playsInline
          muted={false}
          className="w-full h-full object-cover"
          style={{ opacity: isLive ? 1 : 0, transition: "opacity 0.5s ease" }}
        />
      </div>

      {/* ── Loading overlay ──────────────────────────────────────────── */}
      {isLoading && (
        <div className={`absolute inset-0 z-20 flex flex-col items-center justify-center ${theme === "light" ? "bg-gradient-to-b from-gray-100 to-gray-200" : "bg-gradient-to-b from-gray-800 to-gray-900"}`}>
          <div className="relative mb-10 w-24 h-24">
            <div
              className={`absolute inset-[-8px] rounded-full border-2 ${theme === "light" ? "border-blue-500/25" : "border-blue-400/25"}`}
              style={{ animation: "ring-pulse 2.5s ease-in-out infinite" }}
            />
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center animate-pulse">
                <svg className={`w-12 h-12 ${theme === "light" ? "text-blue-500/50" : "text-blue-400/60"}`} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
            </div>
          </div>
          <p className={`text-base font-medium mb-2 ${theme === "light" ? "text-gray-600" : "text-gray-300"}`}>
            {CONNECTING_STEPS[connectingStep]}
          </p>
          <div className="flex space-x-2 mt-2">
            {CONNECTING_STEPS.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === connectingStep
                    ? "bg-blue-400 scale-125"
                    : theme === "light" ? "bg-gray-300" : "bg-gray-600"
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Error overlay ────────────────────────────────────────────── */}
      {phase === PHASES.ERROR && (
        <div className={`absolute inset-0 z-20 flex items-center justify-center ${theme === "light" ? "bg-[#FAF9F6]" : "bg-gray-900"}`}>
          <div className="flex flex-col items-center p-6 bg-gradient-to-r from-red-100 to-pink-200 border-l-4 border-red-500 rounded-xl shadow-xl max-w-lg mx-auto">
            <p className="text-red-800 text-center font-semibold tracking-wide mb-4">{errorMessage}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* ── Live UI — only shown when avatar is active ───────────────── */}
      {isLive && (
        <>
          {/* Candidate camera PIP — bottom-right */}
          <div
            className={`absolute bottom-5 right-5 z-10 rounded-xl overflow-hidden shadow-2xl border-2 ${
              theme === "light" ? "border-gray-300" : "border-white/20"
            }`}
            style={{ width: PIP_WIDTH, height: PIP_HEIGHT }}
          >
            <video
              ref={pipVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
          </div>

          {/* Status bar — bottom center; shows hint for first 10s, then only on BAD connection */}
          {(connectionQuality === "BAD" || showStatusHint) && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10">
              <div className={`backdrop-blur-sm px-5 py-2 rounded-full flex items-center gap-2 ${theme === "light" ? "bg-white/80 shadow-md" : "bg-black/50"}`}>
                {connectionQuality === "BAD" ? (
                  <>
                    <svg className="w-4 h-4 text-amber-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <p className={`text-sm font-medium ${theme === "light" ? "text-amber-600" : "text-amber-400"}`}>
                      Poor connection — your video may be delayed
                    </p>
                  </>
                ) : (
                  <p className={`text-sm ${theme === "light" ? "text-gray-600" : "text-gray-200"}`}>
                    Speak clearly — your interviewer can hear you
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Proctoring alert modal */}
          {showProctoringAlert && (
            <ModalOverlay>
              <ProctoringAlertModal
                isOpen={true}
                message={proctoringAlertMessage}
                onAcknowledge={handleProctoringAcknowledge}
                candidateName={state.candidateName}
              />
            </ModalOverlay>
          )}
        </>
      )}

      {/* Hidden proctoring camera — AI detection only; opacity:0 keeps stream active */}
      <div style={{ position: "fixed", top: 0, left: 0, opacity: 0, pointerEvents: "none", zIndex: -1 }}>
        <CameraView
          videoIncluded={true}
          proctoringVideoRef={proctoringVideoRef}
          onCameraReady={noop}
          isInterviewActive={isInterviewActive}
          interviewId={interview_id}
          onStickyAlert={handleStickyAlert}
          onFaceViolation={handleFaceViolation}
          onMultipleFaceViolation={handleMultipleFaceViolation}
          onProhibitedObjectViolation={handleProhibitedObjectViolation}
          onBodyPostureViolation={handleBodyPostureViolation}
          onGazeViolation={handleGazeViolation}
          onFullscreenChange={noop}
          onTabSwitchViolation={handleTabSwitchViolation}
          onFullscreenExitViolation={handleFullscreenExitViolation}
        />
      </div>

      {/* Screen-share-stop modal — blocks interview until sharing is resumed */}
      {isLive && !isScreenSharing && isTabSwitchingEnabled && (
        <ModalOverlay>
          <ProctoringAlertModal
            isOpen={true}
            message="Please Resume Screen Sharing to Continue the Interview"
            onAcknowledge={async () => {
              setIsIntentionalFullscreenChange(true);
              await requestScreenPermission()
                .then(() => handleScreenShareStopedViolation());
            }}
            candidateName={state.candidateName}
          />
        </ModalOverlay>
      )}

      {/* Debug console — dev mode only, never shown in production */}
      {IS_DEV && (
        <DebugConsole
          logs={debugLogs}
          onClear={() => setDebugLogs([])}
        />
      )}

      {/* Begin Interview gate — shown when page opened directly (not via fullscreen instructions flow) */}
      {!readyToInit && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className={`relative w-full max-w-lg mx-4 sm:mx-6 rounded-2xl shadow-2xl overflow-hidden ${theme === "light" ? "bg-white border border-gray-200" : "bg-gradient-to-b from-gray-900 to-gray-800 border border-gray-700"}`}>
            <div className="px-8 py-8 text-center flex flex-col items-center gap-4">
              <h2 className={`text-2xl font-bold tracking-tight ${theme === "light" ? "text-gray-900" : "text-white"}`}>
                Ready to Begin?
              </h2>
              <p className={`text-sm ${theme === "light" ? "text-gray-600" : "text-gray-400"}`}>
                Your interview will start as soon as you click below.
              </p>
              <button onClick={handleBeginInterview} className="button mt-2">
                <span>Begin Interview</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert panel — bottom-left, always mounted */}
      <AlertPanel
        getActiveAlerts={getActiveAlerts}
        alertLog={alertLog}
        stickyAlertCount={stickyAlertCount}
        isAlertPanelOpen={isAlertPanelOpen}
        handleAlertIconClick={handleAlertIconClick}
        currentAlertIndex={currentAlertIndex}
        navigateToPrevAlert={navigateToPrevAlert}
        navigateToNextAlert={navigateToNextAlert}
        handleAlertClick={handleAlertClick}
      />
    </div>
  );
}
